import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "../components/ui/button";
import { DraggableCryptoPairs } from "../components/home/DraggableCryptoPairs";
import { useAuth } from "../hooks/useAuth";
import * as marketService from "../services/marketService";
import type { ChartRange, MarketCategory } from "../services/marketService";
import * as projectService from "../services/projectService";
import type { MarketPair, Project } from "../types/api";
import { useTranslation } from "react-i18next";
import {
  pickProjectDescription,
  pickProjectName,
} from "../utils/localeDisplay";

type SnapshotEntry = Pick<
  MarketPair,
  "symbol" | "price" | "changePercent24h" | "projectName"
>;

const sparklineData = [
  { name: "Lun", value: 12 },
  { name: "Mar", value: 14 },
  { name: "Mer", value: 11 },
  { name: "Jeu", value: 16 },
  { name: "Ven", value: 18 },
  { name: "Sam", value: 17 },
  { name: "Dim", value: 20 },
];

const adoptionData = [
  { month: "Jan", users: 120 },
  { month: "Fév", users: 180 },
  { month: "Mar", users: 250 },
  { month: "Avr", users: 320 },
  { month: "Mai", users: 420 },
  { month: "Jun", users: 540 },
];

const volumeData = [
  { label: "Lun", volume: 120 },
  { label: "Mar", volume: 160 },
  { label: "Mer", volume: 90 },
  { label: "Jeu", volume: 140 },
  { label: "Ven", volume: 220 },
  { label: "Sam", volume: 180 },
  { label: "Dim", volume: 200 },
];

type ScrollStepVisual = { type: "video"; src: string } | { type: "draggable" };

interface ScrollStep {
  id: string;
  stepLabel: string;
  kicker: string;
  title: string;
  description: string;
  visual: ScrollStepVisual;
}

const ScrollDrivenSteps = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const scrollSteps = useMemo<ScrollStep[]>(
    () => [
      {
        id: "install",
        stepLabel: t("home.steps.install.stepLabel"),
        kicker: t("home.steps.install.kicker"),
        title: t("home.steps.install.title"),
        description: t("home.steps.install.description"),
        visual: {
          type: "video",
          src: "/assets/images/AI_Trading_Synapse_Network.mp4",
        },
      },
      {
        id: "topup",
        stepLabel: t("home.steps.topup.stepLabel"),
        kicker: t("home.steps.topup.kicker"),
        title: t("home.steps.topup.title"),
        description: t("home.steps.topup.description"),
        visual: { type: "draggable" },
      },
      {
        id: "activate",
        stepLabel: t("home.steps.activate.stepLabel"),
        kicker: t("home.steps.activate.kicker"),
        title: t("home.steps.activate.title"),
        description: t("home.steps.activate.description"),
        visual: {
          type: "video",
          src: "/assets/images/Futuristic_Trading_Robot_Command_Center.mp4",
        },
      },
      {
        id: "execute",
        stepLabel: t("home.steps.execute.stepLabel"),
        kicker: t("home.steps.execute.kicker"),
        title: t("home.steps.execute.title"),
        description: t("home.steps.execute.description"),
        visual: {
          type: "video",
          src: "/assets/images/Neural_Trade_Robot_Visualizer.mp4",
        },
      },
      {
        id: "results",
        stepLabel: t("home.steps.results.stepLabel"),
        kicker: t("home.steps.results.kicker"),
        title: t("home.steps.results.title"),
        description: t("home.steps.results.description"),
        visual: {
          type: "video",
          src: "/assets/images/vid1.mp4",
        },
      },
    ],
    [t],
  );

  useEffect(() => {
    const handleScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const totalScrollable = rect.height - viewportHeight;
      if (totalScrollable <= 0) {
        setProgress(0);
        return;
      }
      const scrolled = Math.min(Math.max(-rect.top, 0), totalScrollable);
      setProgress(scrolled / totalScrollable);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const stepFloat = progress * (scrollSteps.length - 1);
  const activeIndex = Math.min(
    scrollSteps.length - 1,
    Math.max(0, Math.round(stepFloat)),
  );
  // Déplacement horizontal continu en fonction du scroll
  const translateX = -stepFloat * 100;

  const scrollToStep = (index: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || 1;
    const totalScrollable = el.offsetHeight - viewportHeight;
    if (totalScrollable <= 0) return;
    const ratio = scrollSteps.length > 1 ? index / (scrollSteps.length - 1) : 0;
    const target = window.scrollY + rect.top + totalScrollable * ratio;
    window.scrollTo({ top: target, behavior: "smooth" });
  };

  return (
    <section
      ref={containerRef}
      className="relative mt-16 h-[260vh]"
      aria-label="Comment SYNAPSE orchestre vos trades étape par étape">
      <div className="sticky top-16 h-[50vh] overflow-hidden rounded-3xl border border-slate-200 bg-white/95 shadow-xl dark:border-white/10 dark:bg-slate-950/90 dark:shadow-2xl md:top-24 md:h-[55vh]">
        <div
          className="flex h-full will-change-transform transition-transform duration-500 ease-out"
          style={{ transform: `translateX(${translateX}%)` }}>
          {scrollSteps.map((step, index) => {
            const distance = Math.abs(stepFloat - index);
            const opacity = Math.max(0, Math.min(1, 1 - distance));
            return (
              <div
                key={step.id}
                className="flex w-full flex-shrink-0 flex-col gap-4 px-4 py-4 text-slate-900 transition-opacity duration-300 dark:text-white lg:flex-row lg:items-stretch lg:gap-6 lg:px-8 lg:py-6"
                style={{
                  opacity,
                  pointerEvents: distance < 0.5 ? "auto" : "none",
                }}>
                <div className="flex w-full flex-col justify-center lg:w-1/2">
                  <p className="text-sm font-semibold uppercase tracking-[0.22em] text-amber-300">
                    {step.kicker}
                  </p>
                  <h3 className="mt-3 text-2xl font-semibold leading-tight sm:text-3xl md:text-4xl lg:text-5xl">
                    {step.title}
                  </h3>
                  <p className="mt-3 max-w-2xl text-sm text-slate-600 dark:text-white/70 sm:text-base md:mt-5">
                    {step.description}
                  </p>
                </div>
                <div className="relative w-full lg:w-1/2">
                  <div className="relative h-full min-h-[200px] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100/80 shadow-[0_0_40px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-black/40 dark:shadow-[0_0_50px_rgba(8,47,73,0.65)] sm:min-h-[240px]">
                    {step.visual.type === "video" ? (
                      <video
                        src={step.visual.src}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="h-full w-full object-contain opacity-95"
                      />
                    ) : (
                      <div className="relative h-full w-full">
                        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/80 to-slate-900" />
                        <div className="absolute inset-0 opacity-70">
                          <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
                          <div className="absolute -right-20 -bottom-24 h-72 w-72 rounded-full bg-emerald-500/15 blur-3xl" />
                        </div>
                        <div className="relative h-full w-full p-3">
                          <DraggableCryptoPairs className="h-full w-full" />
                        </div>
                        <div className="pointer-events-none absolute inset-x-6 top-6 flex items-center justify-between text-xs text-white/60">
                          <span className="font-semibold uppercase tracking-[0.2em] text-white/70">
                            Drag & drop pairs
                          </span>
                          <span className="text-emerald-300">LIVE</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Barre de steps (synapse animée) - juste en dessous des cards, fixe au scroll */}
      <div className="sticky top-[calc(4rem+50vh)] z-10 border-t border-slate-200 bg-white/85 backdrop-blur dark:border-white/10 dark:bg-slate-950/70 md:top-[calc(6rem+55vh)]">
        {/* Synapse SVG animée - pleine largeur */}
        <div className="relative h-20 w-full">
          <svg
            viewBox="0 0 1000 80"
            className="h-full w-full"
            preserveAspectRatio="none">
            {/* 5 lignes qui forment la synapse */}
            {[0, 1, 2, 3, 4].map((i) => {
              // Symétrie : ligne i à gauche correspond à ligne (4-i) à droite
              // Ligne 0 (haut gauche) -> ligne 4 (bas droite)
              // Ligne 1 -> ligne 3
              // Ligne 2 reste au centre
              const mirroredIndex = 4 - i; // Index de la ligne correspondante à droite

              const yStart = 20 + i * 10; // Position de départ à gauche
              const yEnd = 20 + mirroredIndex * 10; // Position d'arrivée à droite (symétrique)
              const yCenter = 40;

              // Zones de la synapse
              const leftZone = 0;
              const convergenceStart = 200;
              const convergenceEnd = 400;
              const synapseCenter = 500;
              const divergenceStart = 600;
              const divergenceEnd = 800;
              const rightZone = 1000;

              // Position actuelle basée sur le progress
              const progressX = progress * 1000;

              // Fonction pour calculer le chemin complet de chaque ligne avec entrelacement symétrique
              const getFullPath = () => {
                // Entrelacement symétrique : les lignes se croisent au centre
                // Ligne 0 (haut) va vers le bas au centre, ligne 4 (bas) va vers le haut
                // Ligne 1 va légèrement vers le bas, ligne 3 va légèrement vers le haut
                // Ligne 2 reste au centre
                const centerOffset = (i - 2) * 10; // Décalage symétrique au centre
                const centerY = yCenter + centerOffset;

                // Points intermédiaires pour créer un vrai entrelacement avec courbes
                const midLeft = convergenceEnd + 20;
                const midRight = divergenceStart - 20;

                // Points de contrôle pour la convergence (gauche vers centre)
                const cp1x = convergenceStart + 50;
                const cp1y = yStart + (centerY - yStart) * 0.4;
                const cp2x = convergenceStart + 100;
                const cp2y = yStart + (centerY - yStart) * 0.6;

                // Zone d'entrelacement : courbe douce qui passe par le centre
                const cp3x = midLeft;
                const cp3y = centerY;
                const cp4x = midRight;
                const cp4y = centerY;

                // Points de contrôle pour la divergence (centre vers droite)
                const cp5x = divergenceStart - 100;
                const cp5y = centerY + (yEnd - centerY) * 0.6;
                const cp6x = divergenceStart - 50;
                const cp6y = centerY + (yEnd - centerY) * 0.4;

                // Chemin complet avec courbes de Bézier et entrelacement symétrique
                return `M ${leftZone} ${yStart} 
                            L ${convergenceStart} ${yStart}
                            C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${midLeft} ${centerY}
                            C ${synapseCenter - 30} ${centerY}, ${
                  synapseCenter + 30
                } ${centerY}, ${midRight} ${centerY}
                            C ${cp5x} ${cp5y}, ${cp6x} ${cp6y}, ${divergenceEnd} ${yEnd}
                            L ${rightZone} ${yEnd}`;
              };

              // Couleur basée sur la position dans la synapse
              const getGradientColor = () => {
                if (progressX < convergenceStart) {
                  return "#22d3ee"; // cyan-400 (bleu)
                } else if (progressX < synapseCenter) {
                  // Transition bleu -> violet
                  const t =
                    (progressX - convergenceStart) /
                    (synapseCenter - convergenceStart);
                  return `rgb(${34 + (168 - 34) * t}, ${
                    211 + (85 - 211) * t
                  }, ${238 + (255 - 238) * t})`;
                } else if (progressX < divergenceStart) {
                  return "#a855f7"; // purple-500 (violet au centre)
                } else if (progressX < divergenceEnd) {
                  // Transition violet -> rose
                  const t =
                    (progressX - divergenceStart) /
                    (divergenceEnd - divergenceStart);
                  return `rgb(${168 + (244 - 168) * t}, ${
                    85 + (114 - 85) * t
                  }, ${247 + (182 - 247) * t})`;
                } else {
                  return "#f472b6"; // pink-400 (rose)
                }
              };

              // Opacité basée sur la distance du progress - symétrique
              // Ligne 0 (haut gauche) active au début ET ligne 4 (bas droite) active à la fin
              // Ligne 1 active au début-milieu ET ligne 3 active à la fin-milieu
              // Ligne 2 active au centre
              const lineProgress = progress;

              // Zones d'activation : chaque ligne est active à gauche ET à droite (symétrique)
              const stepSize = 1 / scrollSteps.length;
              const leftZoneStart = i * stepSize * 0.5;
              const leftZoneEnd = (i + 1) * stepSize * 0.5;
              const rightZoneStart = 0.5 + mirroredIndex * stepSize * 0.5;
              const rightZoneEnd = 0.5 + (mirroredIndex + 1) * stepSize * 0.5;

              const leftActive =
                lineProgress >= leftZoneStart && lineProgress <= leftZoneEnd;
              const rightActive =
                lineProgress >= rightZoneStart && lineProgress <= rightZoneEnd;
              const centerActive =
                i === 2 && lineProgress >= 0.35 && lineProgress <= 0.65;

              const isActive = leftActive || rightActive || centerActive;
              const opacity = isActive ? 1 : 0.4;

              return (
                <path
                  key={i}
                  d={getFullPath()}
                  fill="none"
                  stroke={getGradientColor()}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={opacity}
                  className="transition-all duration-500"
                  style={{
                    filter: isActive
                      ? "drop-shadow(0 0 4px currentColor)"
                      : "none",
                  }}
                />
              );
            })}

            {/* Petits cercles colorés au-dessus qui s'illuminent selon le progress */}
            {[
              { x: 150, color: "#22d3ee", progressRange: [0, 0.25] },
              { x: 350, color: "#ef4444", progressRange: [0.2, 0.45] },
              { x: 500, color: "#a855f7", progressRange: [0.4, 0.65] },
              { x: 700, color: "#3b82f6", progressRange: [0.6, 1] },
            ].map((dot, i) => {
              const isActive =
                progress >= dot.progressRange[0] &&
                progress <= dot.progressRange[1];
              return (
                <g key={i}>
                  <circle
                    cx={dot.x}
                    cy={12}
                    r={isActive ? 5 : 4}
                    fill={dot.color}
                    opacity={isActive ? 1 : 0.4}
                    className="transition-all duration-300"
                    style={{
                      filter: isActive
                        ? `drop-shadow(0 0 8px ${dot.color})`
                        : "none",
                    }}
                  />
                  {isActive && (
                    <circle
                      cx={dot.x}
                      cy={12}
                      r={8}
                      fill={dot.color}
                      opacity={0.2}
                      className="animate-pulse"
                    />
                  )}
                </g>
              );
            })}
          </svg>
        </div>
        {/* Steps en dessous */}
        <div className="mx-auto max-w-6xl px-6 py-3">
          <div
            className="grid gap-2"
            style={{
              gridTemplateColumns: `repeat(${scrollSteps.length}, minmax(0, 1fr))`,
            }}>
            {scrollSteps.map((s, i) => {
              const active = i === activeIndex;
              return (
                <div key={s.id} className="group text-left">
                  <div className="flex items-center gap-2">
                    <span
                      className={[
                        "text-xs font-semibold",
                        active ? "text-primary dark:text-cyan-300" : "text-slate-400 dark:text-white/35",
                      ].join(" ")}>
                      Step {i + 1}
                    </span>
                    <span
                      className={[
                        "h-1.5 w-1.5 rounded-full",
                        active
                          ? "bg-emerald-500 shadow-[0_0_18px_rgba(16,185,129,0.5)] dark:bg-emerald-300 dark:shadow-[0_0_18px_rgba(16,185,129,0.7)]"
                          : "bg-slate-300 dark:bg-white/20",
                      ].join(" ")}
                      aria-hidden="true"
                    />
                  </div>
                  <div
                    className={[
                      "mt-1 text-sm font-medium",
                      active ? "text-foreground dark:text-white" : "text-slate-500 dark:text-white/35",
                    ].join(" ")}>
                    {s.stepLabel}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const globeImageUrl = `${
    process.env.PUBLIC_URL ?? ""
  }/assets/images/Futuristic_Trading_Robot_AI.png`;
  const [marketSnapshot, setMarketSnapshot] = useState<SnapshotEntry[]>([]);
  const [featuredProjects, setFeaturedProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("overview");
  const [marketOverviewTab, setMarketOverviewTab] =
    useState<MarketCategory>("indices");
  const [marketTimeRange, setMarketTimeRange] = useState<ChartRange>("1Y");
  const [expertExpanded, setExpertExpanded] = useState<Record<number, boolean>>(
    {},
  );
  const [selectedMarketPair, setSelectedMarketPair] =
    useState<SnapshotEntry | null>(null);
  const [chartCandles, setChartCandles] = useState<
    Array<{ time: string; value: number }>
  >([]);
  const [chartCandlesLoading, setChartCandlesLoading] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const insights = useMemo(
    () => [
      {
        title: t("home.insights.items.0.title"),
        excerpt: t("home.insights.items.0.excerpt"),
        author: t("home.insights.items.0.author"),
      },
      {
        title: t("home.insights.items.1.title"),
        excerpt: t("home.insights.items.1.excerpt"),
        author: t("home.insights.items.1.author"),
      },
      {
        title: t("home.insights.items.2.title"),
        excerpt: t("home.insights.items.2.excerpt"),
        author: t("home.insights.items.2.author"),
      },
    ],
    [t],
  );

  const sectionOrder = useMemo(
    () => [
      "overview",
      "experience",
      "metrics",
      "analytics",
      "cases",
      "architecture",
      "performance",
      "marketInsights",
      "modules",
      "insights",
      "faq",
      "cta",
    ],
    [],
  );

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const navButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const fetchMarkets = async () => {
      try {
        const data = await marketService.getMarketPairsByCategory(
          marketOverviewTab,
        );
        const snapshot = data.map((item) => ({
          symbol: item.symbol,
          price: item.price,
          changePercent24h: item.changePercent24h,
          projectName: item.projectName,
        }));
        setMarketSnapshot(snapshot);
        setSelectedMarketPair((prev) => {
          const first = snapshot[0] ?? null;
          if (!prev) return first;
          const stillExists = snapshot.some((s) => s.symbol === prev.symbol);
          return stillExists ? prev : first;
        });
      } catch (error) {
        console.error("Impossible de charger les marchés", error);
        setMarketSnapshot([]);
      }
    };
    void fetchMarkets();
    const interval = setInterval(fetchMarkets, 30_000);
    return () => clearInterval(interval);
  }, [marketOverviewTab]);

  useEffect(() => {
    if (!selectedMarketPair?.symbol) return;
    setChartCandlesLoading(true);
    marketService
      .getChartCandles(selectedMarketPair.symbol, marketTimeRange)
      .then(setChartCandles)
      .catch(() => setChartCandles([]))
      .finally(() => setChartCandlesLoading(false));
  }, [selectedMarketPair?.symbol, marketTimeRange]);

  useEffect(() => {
    let isMounted = true;
    const fetchProjects = async () => {
      setProjectsLoading(true);
      try {
        const projects = await projectService.listProjects();
        if (!isMounted) return;
        const sorted = [...projects].sort((a, b) => {
          if (a.status === b.status) {
            return b.id - a.id;
          }
          if (a.status === "open") return -1;
          if (b.status === "open") return 1;
          if (a.status === "upcoming") return -1;
          if (b.status === "upcoming") return 1;
          return 0;
        });
        setFeaturedProjects(sorted.slice(0, 4));
      } catch (error) {
        console.error("Impossible de charger les projets", error);
        if (isMounted) {
          setFeaturedProjects([]);
        }
      } finally {
        if (isMounted) {
          setProjectsLoading(false);
        }
      }
    };
    void fetchProjects();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const hasUpcoming = featuredProjects.some(
      (project) => project.status === "upcoming" && project.openingDate,
    );
    if (!hasUpcoming) {
      return;
    }
    setNow(new Date());
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, [featuredProjects]);

  const formatCountdown = (openingDate?: string | null) => {
    if (!openingDate) return null;
    const target = new Date(openingDate);
    if (Number.isNaN(target.getTime())) return null;
    const diff = target.getTime() - now.getTime();
    if (diff <= 0) return null;
    const totalSeconds = Math.floor(diff / 1000);
    const totalHours = Math.floor(totalSeconds / 3600);
    const hours = totalHours;
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { hours, minutes, seconds };
  };

  const fallbackSnapshot = useMemo<SnapshotEntry[]>(
    () => [
      {
        symbol: "BTC/USD",
        price: 38250,
        changePercent24h: 1.85,
        projectName: "Bitcoin",
      },
      {
        symbol: "ETH/USD",
        price: 2150,
        changePercent24h: -0.64,
        projectName: "Ethereum",
      },
      {
        symbol: "SOL/USD",
        price: 97.4,
        changePercent24h: 4.12,
        projectName: "Solana",
      },
      {
        symbol: "ADA/USD",
        price: 0.61,
        changePercent24h: 1.23,
        projectName: "Cardano",
      },
    ],
    [],
  );

  const snapshotToRender =
    marketSnapshot.length > 0 ? marketSnapshot : fallbackSnapshot;

  // Section active : repère vertical + bas de page (évite de rester bloqué sur FAQ quand le CTA / le pied sont visibles)
  useEffect(() => {
    const computeActiveSection = () => {
      const vh = window.innerHeight;
      const scrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const nearBottom =
        vh + scrollY >= docHeight - 96;

      if (nearBottom && sectionRefs.current.cta) {
        setActiveSection("cta");
        return;
      }

      const lineY = vh * 0.32;
      let bestId = sectionOrder[0]!;
      let bestDist = Infinity;

      for (const id of sectionOrder) {
        const el = sectionRefs.current[id];
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.bottom <= 0 || r.top >= vh) continue;
        const center = r.top + r.height / 2;
        const dist = Math.abs(center - lineY);
        if (dist < bestDist) {
          bestDist = dist;
          bestId = id;
        }
      }

      if (bestDist === Infinity) {
        for (let i = sectionOrder.length - 1; i >= 0; i--) {
          const id = sectionOrder[i]!;
          const el = sectionRefs.current[id];
          if (!el) continue;
          const r = el.getBoundingClientRect();
          if (r.top < vh * 0.6) {
            bestId = id;
            break;
          }
        }
      }

      setActiveSection(bestId);
    };

    let ticking = false;
    const onScrollOrResize = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        computeActiveSection();
        ticking = false;
      });
    };

    computeActiveSection();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [sectionOrder]);

  useLayoutEffect(() => {
    const btn = navButtonRefs.current[activeSection];
    if (btn) {
      btn.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [activeSection]);

  return (
    <div className="space-y-20 bg-gradient-to-b from-background via-muted/35 to-background px-4 py-12 sm:px-6 md:px-8 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900/90">
      {/* Hero SYNAPSE */}
      <section
        ref={(el) => {
          sectionRefs.current.overview = el;
        }}
        data-section-id="overview"
        className="relative overflow-hidden rounded-3xl border border-border/90 bg-gradient-to-br from-card via-muted/30 to-muted/60 px-8 py-16 shadow-lg shadow-slate-900/[0.06] dark:border-white/5 dark:from-slate-950/50 dark:via-slate-900/30 dark:to-slate-900/20 dark:shadow-none sm:px-12 sm:py-20 md:px-16 md:py-24">
        {/* Backdrop 3D-ish sur toute la zone + montage robot */}
        <div
          className="synapse-hero-scene pointer-events-none"
          aria-hidden="true">
          {/* Image de robot IA + dashboards (à placer dans public/assets/hero-robot-trading.jpg) */}
          {/* <img
            src="/assets/images/AI_Trading_Synapse_Network.mp4"
            alt="AI trading robot with live market dashboards"
            className="synapse-hero-image"
          /> */}
          <video
            src="/assets/images/vidaccu.mp4"
            autoPlay
            muted
            loop
            className="synapse-hero-image"
          />
          <div className="synapse-hero-planet" />
          <div className="synapse-hero-orbit" />
          <div className="synapse-hero-synapse" />
          {/* <div className="synapse-hero-metrics">
            <div className="synapse-hero-metric-pill">
              AI TRADING ENGINE • LIVE SIGNALS
            </div>
            <div className="synapse-hero-metric-pill">
              SYNAPSE NETWORK • 24/7 MONITORING
            </div>
          </div> */}
        </div>

        <div className="relative z-10 flex flex-col gap-12 lg:flex-row lg:items-center">
          <div className="flex-1 space-y-6 text-white">
            <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/95">
              {t("home.heroBadge")}
            </p>
            <h1 className="text-5xl font-light leading-tight tracking-tight text-white [text-shadow:0_2px_24px_rgba(0,0,0,0.45)] sm:text-6xl lg:text-7xl">
              {t("home.heroTitle")}
            </h1>
            <p className="max-w-2xl text-xl font-light leading-relaxed uppercase tracking-[0.25em] text-cyan-100/95 [text-shadow:0_1px_16px_rgba(0,0,0,0.35)]">
              {t("home.heroTagline")}
            </p>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-200/95 [text-shadow:0_1px_12px_rgba(0,0,0,0.4)]">
              {t("home.heroIntro")}
            </p>
            <div className="pt-4 flex flex-wrap gap-4">
              {!isAuthenticated && (
                <Button asChild size="lg">
                  <Link to="/investment-packs">{t("home.heroPrimaryCta")}</Link>
                </Button>
              )}
              <Button
                asChild
                size="lg"
                variant="ghost"
                className="border border-white/25 bg-white/10 text-white shadow-sm backdrop-blur-sm hover:bg-white/18 hover:text-white">
                <Link to={isAuthenticated ? "/dashboard" : "/projects"}>
                  {isAuthenticated
                    ? t("home.heroSecondaryCtaAuthenticated")
                    : t("home.heroSecondaryCta")}
                </Link>
              </Button>
            </div>
          </div>
          {/* Widget marchés côté droit - disposition en cartes */}
          <div className="flex-1 lg:pl-8">
            <div className="rounded-2xl border border-border/90 bg-card/95 p-6 shadow-md shadow-slate-900/[0.07] backdrop-blur-md dark:border-white/5 dark:bg-white/5 dark:shadow-none">
              <div className="mb-6 flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground dark:text-white/50">
                  {t("home.heroMarketsTitle")}
                </span>
                <span className="h-2 w-2 rounded-full bg-emerald-500/80 animate-pulse dark:bg-emerald-400/60" />
              </div>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-2">
                {snapshotToRender.slice(0, 4).map((pair) => (
                  <div
                    key={pair.symbol}
                    className="rounded-xl border border-border/70 bg-muted/40 p-4 dark:border-white/5 dark:bg-white/5">
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-foreground dark:text-white">
                        {pair.symbol}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground dark:text-white/50">
                        {pair.projectName ?? t("home.marketGlobal")}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-light tabular-nums text-foreground dark:text-white">
                        $
                        {pair.price?.toLocaleString("fr-FR", {
                          maximumFractionDigits: 2,
                        })}
                      </p>
                      <p
                        className={`mt-1 text-sm font-medium ${
                          (pair.changePercent24h ?? 0) >= 0
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-red-600 dark:text-red-400"
                        }`}>
                        {(pair.changePercent24h ?? 0) >= 0 ? "+" : ""}
                        {(pair.changePercent24h ?? 0).toFixed(2)}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation de sections — largeur contenu (comme avant), max aligné page, pas toute la ligne */}
      <div className="sticky top-16 z-30 w-full min-w-0 px-2 sm:px-4">
        <div className="relative mx-auto w-full max-w-6xl min-w-0">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-background via-background/90 to-transparent md:hidden" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-background via-background/90 to-transparent md:hidden" />

          <div className="flex justify-center">
            <nav
              className="inline-flex max-w-full flex-nowrap items-center gap-1.5 overflow-x-auto rounded-full border border-border/90 bg-card/95 px-3 py-2 text-sm shadow-lg shadow-slate-900/[0.08] backdrop-blur-xl md:gap-1.5 md:px-3 md:py-1.5 md:text-xs scrollbar-hide dark:border-white/20 dark:bg-black/80 dark:shadow-xl"
              style={{ scrollBehavior: "smooth" }}>
            {sectionOrder.map((id) => (
              <button
                key={id}
                ref={(el) => {
                  navButtonRefs.current[id] = el;
                }}
                type="button"
                aria-current={activeSection === id ? "true" : undefined}
                onClick={() => {
                  const el = sectionRefs.current[id];
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }}
                className={`whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium transition-all active:scale-95 touch-manipulation sm:px-3 md:px-2.5 md:py-1.5 md:text-xs ${
                  activeSection === id
                    ? "scale-105 bg-primary text-primary-foreground shadow-md"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground dark:text-white/90 dark:hover:bg-white/10 dark:hover:text-white"
                }`}>
                {t(`home.sections.${id}`)}
              </button>
            ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Section scroll hijacking / horizontal steps pilotée par le scroll */}
      <ScrollDrivenSteps />

      {/* Contenu principal - version simplifiée */}
      <div className="relative overflow-x-hidden">
        <div
          className="synapse-globe-background pointer-events-none absolute inset-0 opacity-30"
          aria-hidden="true">
          <div
            className="synapse-globe-orb"
            style={{ backgroundImage: `url(${globeImageUrl})` }}
          />
        </div>
        <div className="relative z-10 space-y-24 px-4 sm:px-6 md:px-8">
          {/* Expérience client élevée */}
          <section
            ref={(el) => {
              sectionRefs.current.experience = el;
            }}
            data-section-id="experience"
            className="surface-card grid gap-8 p-8 lg:grid-cols-3 lg:gap-12 lg:p-12">
            {["config", "autonomy", "performance"].map((col) => (
              <div key={col} className="space-y-4">
                <div className="flex items-center gap-3 text-sm font-medium uppercase tracking-wider text-muted-foreground dark:text-white/70">
                  <span className="text-xl">
                    {col === "config" ? "🎯" : col === "autonomy" ? "🤖" : "📈"}
                  </span>
                  <span>
                    {col === "config"
                      ? t("home.experience.configTitle")
                      : col === "autonomy"
                      ? t("home.experience.autonomyTitle")
                      : t("home.experience.performanceTitle")}
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground dark:text-white/60">
                  {col === "config"
                    ? t("home.experience.configIntro")
                    : col === "autonomy"
                    ? t("home.experience.autonomyIntro")
                    : t("home.experience.performanceIntro")}
                </p>
                <ul className="space-y-2.5 text-sm text-foreground/85 dark:text-white/80">
                  {(col === "config"
                    ? [
                        "home.experience.configItem1",
                        "home.experience.configItem2",
                        "home.experience.configItem3",
                        "home.experience.configItem4",
                      ]
                    : col === "autonomy"
                    ? [
                        "home.experience.autonomyItem1",
                        "home.experience.autonomyItem2",
                        "home.experience.autonomyItem3",
                        "home.experience.autonomyItem4",
                      ]
                    : [
                        "home.experience.performanceItem1",
                        "home.experience.performanceItem2",
                        "home.experience.performanceItem3",
                        "home.experience.performanceItem4",
                      ]
                  ).map((key) => (
                    <li key={key} className="flex items-start gap-3">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-400/60" />
                      <span className="leading-relaxed">{t(key)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>

          {/* KPIs */}
          <section
            ref={(el) => {
              sectionRefs.current.metrics = el;
            }}
            data-section-id="metrics"
            className="space-y-8">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground dark:text-white/50">
                {t("home.metrics.badge")}
              </p>
              <h2 className="mt-2 text-3xl font-light tracking-tight text-foreground dark:text-white">
                {t("home.metrics.title")}
              </h2>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  label: t("home.metrics.assetsTracked.label"),
                  value: "120+",
                  detail: t("home.metrics.assetsTracked.detail"),
                  color: "cyan",
                },
                {
                  label: t("home.metrics.institutionalClients.label"),
                  value: "48",
                  detail: t("home.metrics.institutionalClients.detail"),
                  color: "purple",
                },
                {
                  label: t("home.metrics.volumeProcessed.label"),
                  value: "$37M",
                  detail: t("home.metrics.volumeProcessed.detail"),
                  color: "pink",
                },
                {
                  label: t("home.metrics.apiAvailability.label"),
                  value: "99.98%",
                  detail: t("home.metrics.apiAvailability.detail"),
                  color: "emerald",
                },
              ].map((item, index) => {
                const colorClasses = {
                  cyan: {
                    border: "border-cyan-500/25",
                    bg: "bg-gradient-to-br from-cyan-500/[0.12] to-cyan-600/[0.06]",
                    value: "text-cyan-700 dark:text-cyan-400",
                    accent: "text-cyan-600/90 dark:text-cyan-300/80",
                  },
                  purple: {
                    border: "border-purple-500/25",
                    bg: "bg-gradient-to-br from-purple-500/[0.12] to-purple-600/[0.06]",
                    value: "text-purple-700 dark:text-purple-400",
                    accent: "text-purple-600/90 dark:text-purple-300/80",
                  },
                  pink: {
                    border: "border-pink-500/25",
                    bg: "bg-gradient-to-br from-pink-500/[0.12] to-pink-600/[0.06]",
                    value: "text-pink-700 dark:text-pink-400",
                    accent: "text-pink-600/90 dark:text-pink-300/80",
                  },
                  emerald: {
                    border: "border-emerald-500/25",
                    bg: "bg-gradient-to-br from-emerald-500/[0.12] to-emerald-600/[0.06]",
                    value: "text-emerald-700 dark:text-emerald-400",
                    accent: "text-emerald-600/90 dark:text-emerald-300/80",
                  },
                };
                const colors =
                  colorClasses[item.color as keyof typeof colorClasses];
                return (
                  <div
                    key={item.label}
                    className={`rounded-xl border ${colors.border} ${colors.bg} p-6 text-foreground shadow-sm shadow-slate-900/[0.04] backdrop-blur-sm dark:text-white dark:shadow-none`}>
                    <p
                      className={`text-xs font-medium uppercase tracking-wider ${colors.accent}`}>
                      {item.label}
                    </p>
                    <p
                      className={`mt-4 text-4xl font-light tracking-tight ${colors.value}`}>
                      {item.value}
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground dark:text-white/60">{item.detail}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Analytics */}
          <section
            ref={(el) => {
              sectionRefs.current.analytics = el;
            }}
            data-section-id="analytics"
            className="space-y-8">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground dark:text-white/50">
                {t("home.analytics.badge")}
              </p>
              <h2 className="mt-2 text-3xl font-light tracking-tight text-foreground dark:text-white">
                {t("home.analytics.title")}
              </h2>
            </div>
            <div className="grid gap-8 lg:grid-cols-2">
              <div className="surface-card p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground dark:text-white/50">
                      {t("home.analytics.adoption.badge")}
                    </p>
                    <h3 className="mt-1 text-xl font-medium">
                      {t("home.analytics.adoption.title")}
                    </h3>
                  </div>
                  <span className="rounded-full bg-emerald-500/20 px-3 py-1 text-sm text-emerald-300">
                    {t("home.analytics.adoption.growth")}
                  </span>
                </div>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={adoptionData}>
                      <defs>
                        <linearGradient id="adopt" x1="0" y1="0" x2="0" y2="1">
                          <stop
                            offset="0%"
                            stopColor="#22d3ee"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="100%"
                            stopColor="#22d3ee"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="month" stroke="#94a3b8" />
                      <YAxis stroke="#94a3b8" />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="users"
                        stroke="#22d3ee"
                        fill="url(#adopt)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="surface-card p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground dark:text-white/50">
                      {t("home.analytics.volume.badge")}
                    </p>
                    <h3 className="mt-1 text-xl font-medium text-foreground dark:text-white">
                      {t("home.analytics.volume.title")}
                    </h3>
                  </div>
                  <span className="rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground dark:bg-white/10 dark:text-white/70">
                    {t("home.analytics.volume.period")}
                  </span>
                </div>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={volumeData}>
                      <defs>
                        <linearGradient id="volume" x1="0" y1="0" x2="0" y2="1">
                          <stop
                            offset="0%"
                            stopColor="#f472b6"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="100%"
                            stopColor="#f472b6"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="label" stroke="#94a3b8" />
                      <YAxis stroke="#94a3b8" />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="volume"
                        stroke="#f472b6"
                        fill="url(#volume)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </section>

          {/* Cas d'utilisation concrets */}
          <section
            ref={(el) => {
              sectionRefs.current.cases = el;
            }}
            data-section-id="cases"
            className="space-y-10 surface-card space-y-10 p-8 lg:p-12">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground dark:text-white/50">
                  {t("home.useCases.badge")}
                </p>
                <h2 className="mt-2 text-3xl font-light tracking-tight">
                  {t("home.useCases.title")}
                </h2>
              </div>
            </div>
            <div className="grid gap-6 lg:grid-cols-3">
              {["card1", "card2", "card3"].map((id) => (
                <article
                  key={id}
                  className="surface-card flex flex-col p-6">
                  <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">
                    {t(`home.useCases.${id}.label`)}
                  </p>
                  <h3 className="mt-2 text-lg font-semibold">
                    {t(`home.useCases.${id}.title`)}
                  </h3>
                  <p className="mt-3 text-sm text-foreground/85 dark:text-white/80 whitespace-pre-line">
                    {t(`home.useCases.${id}.intro`)}
                  </p>
                  <ul className="mt-3 space-y-1 text-sm text-foreground/85 dark:text-white/80">
                    {[1, 2, 3, 4].map((i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-1 text-cyan-300">✓</span>
                        <span>{t(`home.useCases.${id}.item${i}`)}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </section>

          {/* Architecture SYNAPSE */}
          <section
            ref={(el) => {
              sectionRefs.current.architecture = el;
            }}
            data-section-id="architecture"
            className="space-y-10 surface-card space-y-10 p-8 lg:p-12">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground dark:text-white/60">
                {t("home.architecture.badge")}
              </p>
              <h2 className="mt-1 text-2xl font-semibold">
                {t("home.architecture.title")}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground dark:text-white/70">
                {t("home.architecture.subtitle")}
              </p>
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
              {["layer1", "layer2", "layer3"].map((layer) => (
                <article
                  key={layer}
                  className="rounded-2xl border border-slate-200 bg-white/90 dark:border-white/10 dark:bg-slate-950/60 p-5">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-cyan-300">
                    {t(`home.architecture.${layer}.title`)}
                  </h3>
                  <p className="mt-1 text-xs italic text-muted-foreground dark:text-white/70">
                    {t(`home.architecture.${layer}.visual`)}
                  </p>
                  <p className="mt-3 text-xs font-semibold text-muted-foreground dark:text-white/70">
                    {t("home.architecture.dataHeading")}
                  </p>
                  <ul className="mt-1 space-y-1 text-sm text-foreground/85 dark:text-white/80">
                    {[1, 2, 3, 4].map((i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-[3px] text-cyan-300">-</span>
                        <span>{t(`home.architecture.${layer}.item${i}`)}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </section>

          {/* Section avec animations 3D et vidéo */}
          <section className="relative overflow-hidden surface-card space-y-10 p-8 lg:p-12">
            <div className="grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-12">
              {/* Partie gauche : Animations 3D avec texte animé */}
              <div className="relative space-y-4 md:space-y-6">
                {/* Animations 3D en arrière-plan */}
                <div className="relative h-[250px] w-full overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 sm:h-[300px] md:h-[400px]">
                  {/* Orbes animés */}
                  <div className="absolute inset-0">
                    <div className="absolute -left-20 -top-20 h-64 w-64 animate-pulse rounded-full bg-cyan-500/20 blur-3xl" />
                    <div
                      className="absolute -right-20 -bottom-20 h-80 w-80 animate-pulse rounded-full bg-purple-500/20 blur-3xl"
                      style={{ animationDelay: "1s" }}
                    />
                    <div
                      className="absolute left-1/2 top-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full bg-pink-500/15 blur-3xl"
                      style={{ animationDelay: "2s" }}
                    />
                  </div>

                  {/* Particules flottantes */}
                  <div className="absolute inset-0">
                    {[...Array(12)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute h-2 w-2 rounded-full bg-cyan-400/40"
                        style={{
                          left: `${10 + ((i * 7) % 80)}%`,
                          top: `${10 + ((i * 11) % 80)}%`,
                          animation: `float ${
                            3 + (i % 3)
                          }s ease-in-out infinite`,
                          animationDelay: `${i * 0.2}s`,
                        }}
                      />
                    ))}
                  </div>

                  {/* Lignes de connexion animées */}
                  <svg className="absolute inset-0 h-full w-full opacity-30">
                    {[...Array(5)].map((_, i) => (
                      <line
                        key={i}
                        x1={`${20 + i * 15}%`}
                        y1="20%"
                        x2={`${30 + i * 10}%`}
                        y2="80%"
                        stroke="currentColor"
                        strokeWidth="1"
                        className="text-cyan-400/30"
                        style={{
                          animation: `pulse-line ${
                            2 + i * 0.5
                          }s ease-in-out infinite`,
                          animationDelay: `${i * 0.3}s`,
                        }}
                      />
                    ))}
                  </svg>

                  {/* Texte animé au centre */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <h3 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient sm:text-3xl md:text-4xl">
                        SYNAPSE
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground animate-fade-in dark:text-white/70">
                        {t("home.innovation.subtitle")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contenu texte */}
                <div className="space-y-3 md:space-y-4">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground dark:text-white/50">
                    {t("home.innovation.badge")}
                  </p>
                  <h2 className="text-xl font-medium sm:text-2xl md:text-3xl">
                    {t("home.innovation.title")}
                  </h2>
                  <p className="text-sm leading-relaxed text-muted-foreground dark:text-white/70">
                    {t("home.innovation.description")}
                  </p>
                  <ul className="mt-4 space-y-2 text-sm text-foreground/85 dark:text-white/80">
                    {[
                      t("home.innovation.feature1"),
                      t("home.innovation.feature2"),
                      t("home.innovation.feature3"),
                      t("home.innovation.feature4"),
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="text-cyan-400">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Partie droite : Vidéo */}
              <div className="relative h-[300px] w-full overflow-hidden rounded-xl border border-white/5 bg-black/20 animate-slide-in-right sm:h-[400px] md:h-[500px]">
                <video
                  src="/assets/images/vid8.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
          </section>

          {/* Performance quantifiée & témoignage */}
          <section
            ref={(el) => {
              sectionRefs.current.performance = el;
            }}
            data-section-id="performance"
            className="space-y-10 surface-card space-y-10 p-8 lg:p-12">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground dark:text-white/50">
                {t("home.performance.badge")}
              </p>
              <h2 className="mt-2 text-3xl font-light tracking-tight">
                {t("home.performance.title")}
              </h2>
            </div>
            <div className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr),minmax(0,1.5fr)]">
                <div className="space-y-4 rounded-2xl border border-slate-200 bg-white/95 dark:border-white/10 dark:bg-slate-950/70 p-5">
                  <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground dark:text-white/60">
                    {t("home.performance.badge")}
                  </p>
                  <h3 className="text-xl font-semibold">
                    {t("home.performance.title")}
                  </h3>
                  <div className="mt-3 space-y-2 rounded-xl border border-border bg-muted/40 p-4 text-sm font-mono dark:border-white/15 dark:bg-black/40">
                    <p className="font-semibold text-foreground dark:text-white/80">
                      {t("home.performance.panelTitle")}
                    </p>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <p
                        key={i}
                        className="flex items-center gap-2 text-muted-foreground dark:text-white/75">
                        <span>
                          {i === 1
                            ? "☑️"
                            : i === 2
                            ? "📊"
                            : i === 3
                            ? "🎯"
                            : i === 4
                            ? "⚡"
                            : "📈"}
                        </span>
                        <span>{t(`home.performance.item${i}`)}</span>
                      </p>
                    ))}
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-white/95 dark:border-white/10 dark:bg-slate-950/70 p-5">
                    <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground dark:text-white/60">
                      {t("home.testimonial.badge")}
                    </p>
                    <p className="mt-3 text-sm italic text-muted-foreground dark:text-white/80">
                      “{t("home.testimonial.quote")}”
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground dark:text-white/60">
                      {t("home.testimonial.author")}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white/95 p-5 text-sm text-slate-700 space-y-2 dark:border-white/10 dark:bg-slate-950/70 dark:text-white/80">
                    <p className="font-semibold text-foreground dark:text-white">
                      {t("home.footerMetrics.title")}
                    </p>
                    <ul className="space-y-1">
                      {[1, 2, 3].map((i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="mt-[2px] text-primary dark:text-cyan-300">•</span>
                          <span>{t(`home.footerMetrics.item${i}`)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Narrative blocks */}
          <section className="space-y-8">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground dark:text-white/50">
                {t("home.vision.badge")}
              </p>
              <h2 className="mt-2 text-3xl font-light tracking-tight text-foreground dark:text-white">
                {t("home.vision.title")}
              </h2>
            </div>
            <div className="grid gap-6 lg:grid-cols-3">
              {[
                {
                  title: t("home.vision.connectedDesk.title"),
                  content: t("home.vision.connectedDesk.content"),
                },
                {
                  title: t("home.vision.nativeSecurity.title"),
                  content: t("home.vision.nativeSecurity.content"),
                },
                {
                  title: t("home.vision.hybridExperience.title"),
                  content: t("home.vision.hybridExperience.content"),
                },
              ].map((blok) => (
                <article
                  key={blok.title}
                  className="surface-card rounded-2xl p-6">
                  <p className="text-xs font-medium uppercase tracking-wider text-primary dark:text-cyan-400/80">
                    {t("home.vision.badge")}
                  </p>
                  <h3 className="mt-3 text-xl font-medium text-foreground dark:text-white">{blok.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground dark:text-white/70">
                    {blok.content}
                  </p>
                </article>
              ))}
            </div>
          </section>

          {/* Analyse de marché & Insights (avant Fonctionnalités principales) */}
          <section
            ref={(el) => {
              sectionRefs.current.marketInsights = el;
            }}
            data-section-id="marketInsights"
            className="space-y-8 surface-card space-y-10 p-8 lg:p-12">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-primary dark:text-cyan-400/90">
                {t("home.marketInsights.badge")}
              </p>
              <h2 className="mt-2 text-3xl font-light tracking-tight text-foreground dark:text-white">
                {t("home.marketInsights.title")}
              </h2>
              <p className="mt-2 text-muted-foreground dark:text-white/70">
                {t("home.marketInsights.subtitle")}
              </p>
            </div>
            <div className="grid min-h-[520px] gap-8 lg:grid-cols-[1fr_1fr] lg:items-stretch">
              {/* Panneau gauche : Vue marché en direct */}
              <div className="flex min-h-[480px] flex-col rounded-2xl border border-slate-200 bg-white/95 dark:border-white/10 dark:bg-slate-950/70 p-5 transition-colors duration-200 hover:border-cyan-500/60 hover:shadow-[0_0_0_1px_rgba(34,211,238,0.3)]">
                <h3 className="text-lg font-semibold text-foreground dark:text-white">
                  {t("home.marketInsights.liveOverviewTitle")}
                </h3>
                <div className="flex gap-2 border-b border-border pb-2 dark:border-white/10">
                  {(
                    ["indices", "futures", "bonds", "forex"] as MarketCategory[]
                  ).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setMarketOverviewTab(tab)}
                      className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                        marketOverviewTab === tab
                          ? "bg-primary/15 text-primary dark:bg-cyan-500/20 dark:text-cyan-300"
                          : "text-muted-foreground hover:text-foreground dark:text-white/60 dark:hover:text-white/80"
                      }`}>
                      {t(`home.marketInsights.tabs.${tab}`)}
                    </button>
                  ))}
                </div>
                <div className="h-32">
                  {chartCandlesLoading ? (
                    <div className="flex h-full items-center justify-center text-muted-foreground dark:text-white/50">
                      {t("home.marketInsights.loading")}
                    </div>
                  ) : chartCandles.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartCandles}>
                        <defs>
                          <linearGradient
                            id="marketGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1">
                            <stop
                              offset="0%"
                              stopColor="#22d3ee"
                              stopOpacity={0.4}
                            />
                            <stop
                              offset="100%"
                              stopColor="#22d3ee"
                              stopOpacity={0}
                            />
                          </linearGradient>
                        </defs>
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="#22d3ee"
                          fill="url(#marketGradient)"
                        />
                        <XAxis dataKey="time" hide />
                        <YAxis hide />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "rgba(15,23,42,0.95)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: "8px",
                          }}
                          formatter={(value: number) => [
                            value?.toLocaleString("fr-FR", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 6,
                            }),
                            "",
                          ]}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground dark:text-white/50">
                      {selectedMarketPair
                        ? t("home.marketInsights.loading")
                        : t(
                            "home.marketInsights.selectPair",
                            "Cliquez sur une paire",
                          )}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(["1D", "1M", "3M", "1Y", "5Y", "All"] as ChartRange[]).map(
                    (range) => (
                      <button
                        key={range}
                        type="button"
                        onClick={() => setMarketTimeRange(range)}
                        className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                          marketTimeRange === range
                            ? "bg-primary/15 text-primary dark:bg-white/15 dark:text-white"
                            : "text-muted-foreground hover:text-foreground dark:text-white/60 dark:hover:text-white/80"
                        }`}>
                        {range}
                      </button>
                    ),
                  )}
                </div>
                <ul
                  className="flex min-h-0 flex-shrink-0 flex-col gap-2 overflow-y-auto"
                  style={{ maxHeight: "220px" }}>
                  {marketSnapshot.length > 0
                    ? marketSnapshot.map((item) => (
                        <li
                          key={item.symbol}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedMarketPair(item)}
                          onKeyDown={(e) =>
                            (e.key === "Enter" || e.key === " ") &&
                            setSelectedMarketPair(item)
                          }
                          className={`flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2 transition-colors hover:bg-muted/60 dark:hover:bg-white/5 ${
                            selectedMarketPair?.symbol === item.symbol
                              ? "border-cyan-500/50 bg-cyan-500/10"
                              : "border-border bg-muted/40 dark:border-white/5 dark:bg-black/30"
                          }`}>
                          <div className="flex items-center gap-3">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground dark:bg-white/10 dark:text-white">
                              {item.symbol.slice(0, 2)}
                            </span>
                            <div>
                              <p className="font-medium text-foreground dark:text-white">{item.symbol}</p>
                              <p className="text-xs text-muted-foreground dark:text-white/60">
                                {item.projectName ?? item.symbol}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-medium">
                              {typeof item.price === "number"
                                ? item.price.toLocaleString("fr-FR", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 6,
                                  })
                                : "—"}
                            </p>
                            <p
                              className={`text-xs ${
                                (item.changePercent24h ?? 0) >= 0
                                  ? "text-emerald-400"
                                  : "text-rose-400"
                              }`}>
                              {(item.changePercent24h ?? 0) >= 0 ? "+" : ""}
                              {typeof item.changePercent24h === "number"
                                ? item.changePercent24h.toFixed(2)
                                : "0.00"}
                              %
                            </p>
                          </div>
                        </li>
                      ))
                    : [
                        <li
                          key="empty"
                          className="flex items-center justify-center rounded-lg border border-border bg-muted/50 px-3 py-6 text-muted-foreground dark:border-white/5 dark:bg-black/30 dark:text-white/50"
                          role="status">
                          {t(
                            "home.marketInsights.noData",
                            "Aucune donnée pour cette catégorie",
                          )}
                        </li>,
                      ]}
                </ul>
              </div>
              {/* Panneau droit : Analyse experte (même hauteur que Live Market Overview) */}
              <div className="flex min-h-[480px] flex-col rounded-2xl border border-slate-200 bg-white/95 dark:border-white/10 dark:bg-slate-950/70 p-5 transition-colors duration-200 hover:border-emerald-500/60 hover:shadow-[0_0_0_1px_rgba(52,211,153,0.3)]">
                <h3 className="text-lg font-semibold text-foreground dark:text-white">
                  {t("home.marketInsights.expertTitle")}
                </h3>
                {[0, 1, 2].map((idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-border bg-muted/40 p-4 dark:border-white/5 dark:bg-black/30">
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary dark:bg-cyan-500/20 dark:text-cyan-300">
                        {idx === 0 ? "📄" : idx === 1 ? "⚙" : "🛡"}
                      </span>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-foreground dark:text-white">
                          {t(`home.marketInsights.expertItems.${idx}.title`)}
                        </h4>
                        <p className="mt-1 text-sm text-muted-foreground dark:text-white/70">
                          {t(`home.marketInsights.expertItems.${idx}.excerpt`)}
                        </p>
                        {expertExpanded[idx] && (
                          <p className="mt-2 text-sm text-muted-foreground dark:text-white/60">
                            {t(`home.marketInsights.expertItems.${idx}.full`)}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            setExpertExpanded((prev) => ({
                              ...prev,
                              [idx]: !prev[idx],
                            }))
                          }
                          className="mt-2 text-xs font-medium text-cyan-400 hover:underline">
                          {expertExpanded[idx]
                            ? t("home.marketInsights.showLess")
                            : t("home.marketInsights.readMore")}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <Button
                  asChild
                  className="w-full bg-cyan-600 hover:bg-cyan-500">
                  <Link to="/trading">
                    {t("home.marketInsights.cta")}
                    <span className="ml-2" aria-hidden>
                      →
                    </span>
                  </Link>
                </Button>
              </div>
            </div>
          </section>

          {/* Modules */}
          <section
            ref={(el) => {
              sectionRefs.current.modules = el;
            }}
            data-section-id="modules"
            className="space-y-8">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground dark:text-white/50">
                {t("home.modules.badge")}
              </p>
              <h2 className="mt-2 text-3xl font-light tracking-tight text-foreground dark:text-white">
                {t("home.modules.title")}
              </h2>
            </div>
            <div className="grid gap-6 lg:grid-cols-3">
              {[
                {
                  title: t("home.modules.traderView.title"),
                  description: t("home.modules.traderView.description"),
                },
                {
                  title: t("home.modules.onboarding.title"),
                  description: t("home.modules.onboarding.description"),
                },
                {
                  title: t("home.modules.analysis.title"),
                  description: t("home.modules.analysis.description"),
                },
              ].map((card) => (
                <div
                  key={card.title}
                  className="rounded-3xl border border-border bg-card p-6 text-foreground shadow-sm dark:border-white/10 dark:bg-gradient-to-br dark:from-slate-900 dark:to-slate-950 dark:text-white">
                  <h4 className="text-lg font-semibold">{card.title}</h4>
                  <p className="mt-2 text-sm text-muted-foreground dark:text-white/70">
                    {card.description}
                  </p>
                  <div className="mt-4 h-20">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={sparklineData}>
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="#22d3ee"
                          fill="#22d3ee20"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Insights */}
          <section
            ref={(el) => {
              sectionRefs.current.insights = el;
            }}
            data-section-id="insights"
            className="space-y-8 surface-card space-y-10 p-8 lg:p-12">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground dark:text-white/50">
                  {t("home.insights.badge")}
                </p>
                <h2 className="mt-2 text-3xl font-light tracking-tight text-foreground dark:text-white">
                  {t("home.insights.title")}
                </h2>
              </div>
              {/* <Button
                variant="ghost"
                asChild
                className="border border-white/10">
                <Link to="/projects">{t("home.insights.button")}</Link>
              </Button> */}
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {insights.map((article) => (
                <article
                  key={article.title}
                  className="rounded-2xl border border-border bg-muted/30 p-5 dark:border-white/5 dark:bg-white/5">
                  <p className="text-xs uppercase text-primary dark:text-cyan-300">
                    {article.author}
                  </p>
                  <h4 className="mt-2 text-lg font-semibold text-foreground dark:text-white">
                    {article.title}
                  </h4>
                  <p className="mt-2 text-sm text-muted-foreground dark:text-white/70">
                    {article.excerpt}
                  </p>
                </article>
              ))}
            </div>
            <div className="mt-8 rounded-2xl border border-slate-200 bg-white/95 dark:border-white/10 dark:bg-slate-950/70 p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-semibold">
                  {t("home.insights.projectsTitle")}
                </h3>
                <Button
                  variant="ghost"
                  asChild
                  className="border border-white/10">
                  {/* <Link to="/projects">{t("home.insights.projectsCta")}</Link> */}
                  <Link to="/projects">{t("home.insights.button")}</Link>
                </Button>
              </div>
              {projectsLoading ? (
                <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="h-28 animate-pulse rounded-xl border border-border bg-muted/50 dark:border-white/5 dark:bg-white/5"
                    />
                  ))}
                </div>
              ) : featuredProjects.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground dark:text-white/60">
                  {t("home.insights.projectsEmpty")}
                </p>
              ) : (
                <div className="mt-4 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                  {featuredProjects.map((project) => {
                    const statusLabel =
                      project.status === "open"
                        ? t("projects.statusOpen")
                        : project.status === "upcoming"
                        ? t("projects.statusUpcoming")
                        : t("projects.statusClosed");
                    const statusStyles =
                      project.status === "open"
                        ? "border-emerald-300 bg-emerald-500/10 text-emerald-800 dark:border-emerald-400/40 dark:text-emerald-200"
                        : project.status === "upcoming"
                        ? "border-cyan-300 bg-cyan-500/10 text-cyan-800 dark:border-cyan-400/40 dark:text-cyan-200"
                        : "border-slate-300 bg-slate-500/10 text-slate-800 dark:border-slate-400/40 dark:text-slate-200";
                    const countdown = formatCountdown(project.openingDate);
                    const displayName = pickProjectName(project, i18n.language);
                    const displayDescription = pickProjectDescription(
                      project,
                      i18n.language
                    );
                    return (
                      <Link
                        key={project.id}
                        to={`/projects/${project.id}`}
                        className="group rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/90 to-card p-5 transition hover:border-amber-300 hover:shadow-lg dark:border-amber-400/40 dark:from-amber-400/15 dark:via-slate-950/85 dark:to-slate-950/90 dark:hover:border-amber-300/60 dark:hover:shadow-amber-500/20">
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground dark:text-white/60">
                          <span className="uppercase tracking-wide">
                            {project.symbol}
                          </span>
                          <span
                            className={`rounded-full border px-2 py-0.5 text-[11px] ${statusStyles}`}>
                            {statusLabel}
                          </span>
                        </div>
                        {project.status === "upcoming" && countdown && (
                          <div className="mt-3 inline-flex flex-wrap items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold text-amber-200">
                            <span>
                              {t("home.insights.projectsCountdown", {
                                hours: countdown.hours,
                                minutes: countdown.minutes,
                                seconds: countdown.seconds,
                              })}
                            </span>
                          </div>
                        )}
                        <div className="mt-4 flex h-28 items-center justify-center overflow-hidden rounded-xl border border-amber-300/40 bg-amber-50/80 dark:border-amber-400/20 dark:bg-slate-950/70">
                          {project.imageUrl ? (
                            <img
                              src={project.imageUrl}
                              alt={project.name}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-amber-400/10 text-lg font-semibold text-amber-200">
                              {project.symbol.slice(0, 2)}
                            </div>
                          )}
                        </div>
                        <h4 className="mt-4 text-base font-semibold text-foreground group-hover:text-primary dark:text-white dark:group-hover:text-amber-200">
                          {displayName}
                        </h4>
                        <p className="mt-2 text-xs text-muted-foreground dark:text-white/60 line-clamp-2">
                          {displayDescription}
                        </p>
                        {/* <div className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-amber-200">
                          <span>{t("home.insights.projectsCta")}</span>
                          <span aria-hidden>→</span>
                        </div> */}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* FAQ */}
          <section
            ref={(el) => {
              sectionRefs.current.faq = el;
            }}
            data-section-id="faq"
            className="relative space-y-8 overflow-hidden surface-card space-y-10 p-8 lg:p-12">
            {/* Animation de vagues en arrière-plan - style PlayStation portable */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-30">
              <svg
                className="absolute bottom-0 left-0 h-full w-[200%]"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 2880 320"
                preserveAspectRatio="none">
                {/* Vague 1 - dupliquée pour effet infini */}
                <g className="animate-wave-1">
                  <path
                    fill="url(#waveGradient1)"
                    d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,165.3C1248,149,1344,107,1392,85.3L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                  />
                  <path
                    fill="url(#waveGradient1)"
                    d="M1440,96L1488,112C1536,128,1632,160,1728,160C1824,160,1920,128,2016,122.7C2112,117,2208,139,2304,154.7C2400,171,2496,181,2592,165.3C2688,149,2784,107,2832,85.3L2880,64L2880,320L2832,320C2784,320,2688,320,2592,320C2496,320,2400,320,2304,320C2208,320,2112,320,2016,320C1920,320,1824,320,1728,320C1632,320,1536,320,1488,320L1440,320Z"
                  />
                </g>
                {/* Vague 2 - dupliquée pour effet infini */}
                <g className="animate-wave-2">
                  <path
                    fill="url(#waveGradient2)"
                    d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,213.3C672,224,768,224,864,208C960,192,1056,160,1152,154.7C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                  />
                  <path
                    fill="url(#waveGradient2)"
                    d="M1440,224L1488,213.3C1536,203,1632,181,1728,181.3C1824,181,1920,203,2016,213.3C2112,224,2208,224,2304,208C2400,192,2496,160,2592,154.7C2688,149,2784,171,2832,181.3L2880,192L2880,320L2832,320C2784,320,2688,320,2592,320C2496,320,2400,320,2304,320C2208,320,2112,320,2016,320C1920,320,1824,320,1728,320C1632,320,1536,320,1488,320L1440,320Z"
                  />
                </g>
                {/* Vague 3 - dupliquée pour effet infini */}
                <g className="animate-wave-3">
                  <path
                    fill="url(#waveGradient3)"
                    d="M0,160L48,165.3C96,171,192,181,288,186.7C384,192,480,192,576,181.3C672,171,768,149,864,138.7C960,128,1056,128,1152,133.3C1248,139,1344,149,1392,154.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                  />
                  <path
                    fill="url(#waveGradient3)"
                    d="M1440,160L1488,165.3C1536,171,1632,181,1728,186.7C1824,192,1920,192,2016,181.3C2112,171,2208,149,2304,138.7C2400,128,2496,128,2592,133.3C2688,139,2784,149,2832,154.7L2880,160L2880,320L2832,320C2784,320,2688,320,2592,320C2496,320,2400,320,2304,320C2208,320,2112,320,2016,320C1920,320,1824,320,1728,320C1632,320,1536,320,1488,320L1440,320Z"
                  />
                </g>
                <defs>
                  <linearGradient
                    id="waveGradient1"
                    x1="0%"
                    y1="0%"
                    x2="0%"
                    y2="100%">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity="0.2" />
                  </linearGradient>
                  <linearGradient
                    id="waveGradient2"
                    x1="0%"
                    y1="0%"
                    x2="0%"
                    y2="100%">
                    <stop offset="0%" stopColor="#a855f7" stopOpacity="0.3" />
                    <stop
                      offset="100%"
                      stopColor="#f472b6"
                      stopOpacity="0.15"
                    />
                  </linearGradient>
                  <linearGradient
                    id="waveGradient3"
                    x1="0%"
                    y1="0%"
                    x2="0%"
                    y2="100%">
                    <stop offset="0%" stopColor="#f472b6" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.1" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div className="relative z-10">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground dark:text-white/50">
                  {t("home.faq.badge")}
                </p>
                <h2 className="mt-2 text-3xl font-light tracking-tight">
                  {t("home.faq.title")}
                </h2>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  {
                    question: t("home.faq.question1"),
                    answer: t("home.faq.answer1"),
                  },
                  {
                    question: t("home.faq.question2"),
                    answer: t("home.faq.answer2"),
                  },
                  {
                    question: t("home.faq.question3"),
                    answer: t("home.faq.answer3"),
                  },
                  {
                    question: t("home.faq.question4"),
                    answer: t("home.faq.answer4"),
                  },
                ].map((faq) => (
                  <article
                    key={faq.question}
                    className="rounded-xl border border-border bg-muted/25 p-6 backdrop-blur-sm dark:border-white/5 dark:bg-white/5">
                    <h4 className="text-lg font-medium text-foreground dark:text-white/90">
                      {faq.question}
                    </h4>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground dark:text-white/70">
                      {faq.answer}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section
            ref={(el) => {
              sectionRefs.current.cta = el;
            }}
            data-section-id="cta"
            className="rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-muted to-card p-8 text-foreground shadow-sm lg:p-12 dark:border-white/5 dark:bg-gradient-to-r dark:from-cyan-500/15 dark:via-purple-500/20 dark:to-slate-900 dark:text-white dark:shadow-none">
            <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-primary dark:text-white/70">
                  {t("home.cta.badge")}
                </p>
                <h3 className="mt-2 text-3xl font-light tracking-tight text-foreground dark:text-white">
                  {t("home.cta.title")}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground dark:text-white/70">
                  {t("home.cta.description")}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {!isAuthenticated && (
                  <Button asChild>
                    <Link to="/register">{t("home.cta.button1")}</Link>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  asChild
                  className="border border-white/10">
                  <Link to="/trading">{t("home.cta.button2")}</Link>
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
